import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  BULLETIN_GRID_COLUMNS,
  BULLETIN_DETAIL_FIELDS,
} from '../../constants/personBulletinFormConstants';
import { useBulletinDialog } from '../../hooks/useBulletinDialog';
import { getPersonBulletins, deleteBulletin } from '../../services/bulletinService';

const PersonBulletinsTab = ({ personId, onBulletinsChanged }) => {
  const dispatch = useDispatch();
  const bulletinPermission = usePermissions(PERMISSION_RESOURCES.ALERT);
  const [loading, setLoading] = useState(false);
  const [bulletins, setBulletins] = useState([]);
  const { openBulletinDialog } = useBulletinDialog();
  const [detailTarget, setDetailTarget] = useState(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadBulletins = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPersonBulletins(personId),
      onSuccess: (data) => setBulletins(data || []),
    });
  }, [dispatch, personId]);

  useEffect(() => {
    loadBulletins();
  }, [loadBulletins]);

  const handleSaved = useCallback(() => {
    loadBulletins();
    onBulletinsChanged?.();
  }, [loadBulletins, onBulletinsChanged]);

  const handleAdd = () =>
    openBulletinDialog({
      personId: personId,
      canSave: Boolean(bulletinPermission.Create),
      onSaved: handleSaved,
    });

  const handleEdit = (row) =>
    openBulletinDialog({
      bulletinId: row.ptsAlertID,
      personId: personId,
      canSave: Boolean(bulletinPermission.Edit),
      onSaved: handleSaved,
    });

  const handleViewDetails = useCallback((row) => setDetailTarget(row), []);
  const handleCloseDetails = useCallback(() => setDetailTarget(null), []);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        'Are you sure you want to delete this bulletin?',
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteBulletin(row.ptsAlertID),
            onSuccess: () => {
              dispatch(notify('Bulletin deleted successfully', 'success'));
              loadBulletins();
              onBulletinsChanged?.();
            },
          });
        },
        'Delete Bulletin'
      );
    },
    [requestConfirm, dispatch, loadBulletins, onBulletinsChanged]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Bulletin"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!bulletinPermission.Create}
        />
      </Box>

      <CommonDataGrid
        rows={bulletins}
        columns={BULLETIN_GRID_COLUMNS}
        getRowId={(row) => row.ptsAlertID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Bulletins Records' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !bulletinPermission.Edit },
          delete: { visible: true, onClick: handleDelete, disabled: !bulletinPermission.Delete },
          custom: [
            {
              icon: <InfoIcon />,
              tooltip: 'View Details',
              onClick: handleViewDetails,
            },
          ],
        }}
      />

      {detailTarget && (
        <RMSCommonDialog
          open={Boolean(detailTarget)}
          title={detailTarget.AlertType || 'Bulletin Details'}
          onClose={handleCloseDetails}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}>
          <Grid container spacing={1}>
            {BULLETIN_DETAIL_FIELDS.map(({ field, label, render }) => (
              <Grid item xs={6} key={field}>
                <Typography variant="caption" color="textSecondary" component="div">
                  {label}
                </Typography>
                <Typography variant="body2">
                  {render ? render(detailTarget[field]) : detailTarget[field] || ''}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonBulletinsTab;
